/**
 * MarkdownRenderer - Retro mission control styled markdown display
 *
 * Renders markdown content with:
 * - Syntax highlighting for code blocks
 * - Mission control aesthetic (amber/cyan/orange accents)
 * - Monospace fonts for code
 * - Clean, minimal styling
 */

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface MarkdownRendererProps {
  content: string
  className?: string
}

function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
        // Headings
        h1: ({ children }) => (
          <h1 className="text-lg font-bold text-retro-amber mb-3 mt-4 first:mt-0 tracking-wide uppercase">
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-base font-bold text-retro-orange mb-2 mt-3 first:mt-0 tracking-wide">
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-sm font-semibold text-retro-cyan mb-2 mt-3 first:mt-0">
            {children}
          </h3>
        ),
        h4: ({ children }) => (
          <h4 className="text-sm font-medium text-retro-muted mb-1 mt-2 first:mt-0">
            {children}
          </h4>
        ),

        // Paragraphs
        p: ({ children }) => (
          <p className="text-dark-600 leading-relaxed mb-2 last:mb-0">{children}</p>
        ),

        // Lists
        ul: ({ children }) => (
          <ul className="list-none space-y-1 mb-2 ml-2">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-inside space-y-1 mb-2 ml-2 text-dark-600">
            {children}
          </ol>
        ),
        li: ({ children }) => (
          <li className="text-dark-600 flex items-start gap-2">
            <span className="text-retro-dim mt-1.5 text-xs">▸</span>
            <span className="flex-1">{children}</span>
          </li>
        ),

        // Code blocks
        code: ({ className, children, ...props }) => {
          const isInline = !className
          if (isInline) {
            return (
              <code
                className="font-mono text-sm bg-retro-surface text-retro-cyan px-1.5 py-0.5 rounded border border-retro-border"
                {...props}
              >
                {children}
              </code>
            )
          }
          return (
            <code
              className={`block font-mono text-sm bg-retro-bg text-retro-cyan p-3 rounded border border-retro-border overflow-x-auto ${className}`}
              {...props}
            >
              {children}
            </code>
          )
        },
        pre: ({ children }) => (
          <pre className="bg-retro-bg rounded border border-retro-border overflow-x-auto mb-3">
            {children}
          </pre>
        ),

        // Blockquotes
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-retro-orange pl-3 my-2 text-retro-muted italic">
            {children}
          </blockquote>
        ),

        // Links
        a: ({ href, children }) => (
          <a
            href={href}
            className="text-retro-cyan hover:text-retro-amber underline underline-offset-2 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        ),

        // Strong/Bold
        strong: ({ children }) => (
          <strong className="font-semibold text-retro-amber">{children}</strong>
        ),

        // Emphasis/Italic
        em: ({ children }) => <em className="italic text-retro-muted">{children}</em>,

        // Horizontal rule
        hr: () => <hr className="border-retro-border my-4" />,

        // Tables
        table: ({ children }) => (
          <div className="overflow-x-auto mb-3">
            <table className="w-full text-sm border border-retro-border rounded">
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-retro-surface text-retro-amber uppercase text-xs tracking-wider">
            {children}
          </thead>
        ),
        tbody: ({ children }) => (
          <tbody className="divide-y divide-retro-border">{children}</tbody>
        ),
        tr: ({ children }) => (
          <tr className="hover:bg-retro-surface/50 transition-colors">{children}</tr>
        ),
        th: ({ children }) => (
          <th className="px-3 py-2 text-left font-semibold">{children}</th>
        ),
        td: ({ children }) => (
          <td className="px-3 py-2 text-dark-600 font-mono">{children}</td>
        ),
      }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

export default MarkdownRenderer
